


import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Container, Typography, IconButton, Divider,
   CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, 
  Paper, Grid, Chip, TextField, Stepper, Step, StepLabel, Alert, MenuItem, Avatar,
  List, ListItem, ListItemIcon, ListItemText, Collapse, Select, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack, PhotoLibrary, Close, CheckCircle, LocalHospital, Person,
  Phone, Edit, ExpandMore, ExpandLess, Image as ImageIcon,
  Description, EventNote, Wc, AccessTime
} from '@mui/icons-material';
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import CustomDialog from "../../CustomDialog";
import addressConfig from "../../Configurations/addressConfig.json";
import PatientDetailsForm from "./PatientDetailsForm";
import { apiRequest } from '../../fetchSetup';


const COMMUNITY_OPTIONS = addressConfig.communities;
const BLOCK_OPTIONS = addressConfig.blocks;

// Delivery Time Configuration - can be moved to a separate JSON file
const DELIVERY_TIME_CONFIG = {
  minHoursFromNow: 2, // Minimum hours from current time
  timeSlotInterval: 30, // Minutes between each time slot
  availableHoursRange: [6, 22], // Available hours (6 AM to 10 PM)
  daysToShow: 3 // Number of days to show slots for
};

// Updated function to upload prescriptions to backend
const uploadPrescriptionToServer = async (files, orderId) => {
  console.log("orderId:::", orderId);
  const formData = new FormData();
  
  formData.append('order_id', orderId);
  
  files.forEach((fileObj) => {
    if (fileObj.file) {
      formData.append('prescriptions', fileObj.file);
    }
  });
  
  try {
    const response = await apiRequest(`${process.env.REACT_APP_PRESCRITON_MEDICINE_API_URL}`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload prescriptions');
    }
    
    const result = await response.json();
    return result.prescription_urls;
  } catch (error) {
    console.error('Error uploading prescriptions:', error);
    throw error;
  }
};

const createPrescriptionOrder = async (orderData) => {
  const response = await apiRequest(`${process.env.REACT_APP_MEDICINE_API_URL}/medicine-orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData)
  });
  
  if (!response.ok) {
    throw new Error('Failed to create order');
  }
  
  return response.json();
};

// Main Component
const PrescriptionUploadScreen = () => {
  const [step, setStep] = useState('upload');
  const [prescriptionFiles, setPrescriptionFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [fileToRemove, setFileToRemove] = useState(null);
  const [loading, setLoading] = useState(false);
  const [patientDetails, setPatientDetails] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [showPatientDetails, setShowPatientDetails] = useState(true);
  const [showPrescriptions, setShowPrescriptions] = useState(true);
  const [deliveryTimeOption, setDeliveryTimeOption] = useState('anytime');
  const [selectedDate, setSelectedDate] = useState('');
  const [deliveryTimeSlots, setDeliveryTimeSlots] = useState([]);
  const [selectedDeliveryTime, setSelectedDeliveryTime] = useState('');
  const [medicineNames, setMedicineNames] = useState([{ id: Date.now(), name: '' }]);
  const [dialog, setDialog] = useState({ 
    open: false, 
    message: "", 
    title: "",
    type: "info" 
  });
  
  const [address, setAddress] = useState({
    full_name: '',
    phone: '',
    community_name: '',
    address_line1: '',
    address_line2: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    address_type: 'home'
  });


  const [order, setOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({});

// Generate time slots when date or option changes
useEffect(() => {
  setDeliveryTimeSlots([]);
  setSelectedDeliveryTime(''); 
  
  if (deliveryTimeOption === 'specific' && selectedDate) {
    const slots = generateTimeSlotsForDate(selectedDate);
    if (slots.length > 0) {
      console.log('   First:', slots[0].label);
      console.log('   Last:', slots[slots.length - 1].label);
    }
    setDeliveryTimeSlots(slots);
  }
}, [deliveryTimeOption, selectedDate]);


useEffect(() => {
  if (patientDetails && patientDetails.name && patientDetails.phone) {
    console.log(' Syncing patient details to address:', patientDetails.name, patientDetails.phone);
    setAddress(prev => ({
      ...prev,
      full_name: patientDetails.name,
      phone: patientDetails.phone
    }));
  }
}, [patientDetails]);



const handleAddMedicine = () => {
  setMedicineNames(prev => [...prev, { id: Date.now(), name: '' }]);
};

const handleRemoveMedicine = (id) => {
  if (medicineNames.length === 1) return; // Keep at least one field
  setMedicineNames(prev => prev.filter(m => m.id !== id));
};

const handleMedicineChange = (id, value) => {
  setMedicineNames(prev => prev.map(m => m.id === id ? { ...m, name: value } : m));
};


const validatePhone = (phone) => {
  if (!phone) return "Phone number is required";

  if (phone.length !== 10) return "Phone number must be 10 digits";

  // Indian mobile numbers usually start with 6–9
  if (!/^[6-9]\d{9}$/.test(phone)) {
    return "Enter a valid phone number";
  }

  return "";
};







  const roundUpToInterval = (date, intervalMinutes) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    const remainder = minutes % intervalMinutes;
    if (remainder !== 0) {
      rounded.setMinutes(minutes + (intervalMinutes - remainder));
    }
    rounded.setSeconds(0, 0);
    return rounded;
  };
  
// Generate time slots for a specific date
const generateTimeSlotsForDate = (dateString, config = DELIVERY_TIME_CONFIG) => {
  const slots = [];
  const now = new Date();
  // Calculate minimum delivery time
  const minDeliveryRaw = new Date(now.getTime() + config.minHoursFromNow * 60 * 60 * 1000);
  const minDeliveryTime = roundUpToInterval(minDeliveryRaw, config.timeSlotInterval);
  // Parse selected date
  const parts = dateString.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]);
  const day = parseInt(parts[2]);
  const selectedDate = new Date(year, month - 1, day, 0, 0, 0, 0);

  // Check if today
  const nowDateStr = now.toDateString();
  const selectedDateStr = selectedDate.toDateString();
  const isToday = nowDateStr === selectedDateStr;

  let slotsAdded = [];
  let slotsSkipped = [];

  for (let hour = config.availableHoursRange[0]; hour < config.availableHoursRange[1]; hour++) {
    for (let minute = 0; minute < 60; minute += config.timeSlotInterval) {
      // Create slot time
      const slotTime = new Date(year, month - 1, day, hour, minute, 0, 0);
      const slotTimestamp = slotTime.getTime();
      const minTimestamp = minDeliveryTime.getTime();
      
      const slotLabel = slotTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Decision logic
      if (isToday) {
        const shouldSkip = slotTimestamp <= minTimestamp;

        if (shouldSkip) {
          slotsSkipped.push(slotLabel);
          continue;
        } else {
          slotsAdded.push(slotLabel);
        }
      } else {
        slotsAdded.push(slotLabel);
      }

      slots.push({
        value: slotTime.toISOString(),
        label: slotLabel,
        fullDate: slotTime
      });
    }
  }

  return slots;
};


// Generate available dates for next N days (starting from TODAY)
const generateAvailableDates = (daysAhead = 7) => {
  const dates = [];
  const now = new Date();
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  for (let i = 0; i < daysAhead; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    
    // Format the date value as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateValue = `${year}-${month}-${day}`;
    
    // Create display label
    let label = date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
    
    // Add helpful prefix for today and tomorrow
    if (i === 0) {
      label = ` Today - ${label}`;
    } else if (i === 1) {
      label = ` Tomorrow - ${label}`;
    }
        
    dates.push({
      value: dateValue,
      label: label,
      fullDate: date
    });
  }
  
  return dates;
};
  // Load user data and prepopulate address
  useEffect(() => {
    const loadUserData = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      if (!userId || !token) {
        setUser(null);
        return;
      }
      
      try {
        const userData = await fetchUserFromRedis(userId, token);
        setUser(userData || null);
        
        if (userData && userData.address) {
          setAddress({
            full_name: userData.address.full_name || userData.name || '',
            phone: userData.address.phone || userData.phone || '',
            community_name: userData.address.community_name || '',
            address_line1: userData.address.address_line1 || '',
            address_line2: userData.address.address_line2 || '',
            landmark: userData.address.landmark || '',
            city: userData.address.city || '',
            state: userData.address.state || '',
            pincode: userData.address.pincode || '',
            address_type: userData.address.address_type || 'home'
          });
        } else if (userData) {
          console.log(" No full address, using partial data");
          setAddress(prev => ({
            ...prev,
            full_name: userData.name || '',
            phone: userData.phone || ''
          }));
        }
      } catch (err) {
        console.error("❌ Error fetching user:", err);
        setUser(null);
      }
    };

    loadUserData();
  }, [patientDetails]);
  const handlePatientDetailsComplete = (details) => {
    setPatientDetails(details);
    // setStep('upload');
    setStep('address');
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const validFiles = [];
    files.forEach(file => {
      const isValid = file.type.startsWith('image/') || 
                      file.type === 'application/pdf' ||
                      file.type.includes('word');
      if (isValid) validFiles.push(file);
    });

    if (validFiles.length === 0) {
      setDialog({ 
        open: true, 
        type: "error",
        title: "File Selection", 
        message: "Invalid file format. Please upload JPG, PNG, PDF, DOC, or DOCX files." 
      });
      return;
    }

    setUploading(true);
    
    const filePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            size: file.size,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            base64: reader.result,
            file: file
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then(results => {
      setPrescriptionFiles(prev => [...prev, ...results]);
      setUploading(false);
    });

    event.target.value = '';
  };

  const handleRemoveFile = (fileId) => {
    setFileToRemove(fileId);
    setRemoveDialogOpen(true);
  };

  const confirmRemoveFile = () => {
    setPrescriptionFiles(prev => prev.filter(f => f.id !== fileToRemove));
    setRemoveDialogOpen(false);
    setFileToRemove(null);
  };
  const handleProceedToAddress = () => {
    const hasMedicines = medicineNames.some(m => m.name.trim() !== '');
    if (prescriptionFiles.length === 0 && !hasMedicines) {
      setDialog({ 
        open: true, 
        type: "warning",
        title: "Input Required", 
        message: "Please upload at least one prescription OR enter at least one medicine name to continue." 
      });
      return;
    }
    setStep('patient');
  };
  // const handleProceedToAddress = () => {
  //   if (prescriptionFiles.length === 0) {
  //     setDialog({ 
  //       open: true, 
  //       type: "warning",
  //       title: "Upload Required", 
  //       message: "Please upload at least one prescription file to continue." 
  //     });
  //     return;
  //   }
  //   setStep('patient');
  //   // setStep('address');
  // };
const validateAddressForm = () => {
  const newErrors = {};

  // Phone validation
  if (!address.phone) {
    newErrors.phone = "Phone number is required";
  } else if (address.phone.length !== 10) {
    newErrors.phone = "Phone number must be 10 digits";
  } else if (!/^[6-9]\d{9}$/.test(address.phone)) {
    newErrors.phone = "Enter a valid phone number";
  } 
  // ✅ Full name validation (separate)
  if (!address.full_name || !address.full_name.trim()) {
    newErrors.full_name = "Full Name is required";
  } 

  // Add more fields here if needed (name, address, etc.)

  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
};
  const handleSubmitOrder = async () => {
    if (!address.full_name || !address.phone || !address.community_name || 
        !address.address_line1 || !address.city || !address.state || !address.pincode) {
      setDialog({ 
        open: true, 
        type: "warning",
        title: "Incomplete Address", 
        message: "Please fill all required fields to submit your order." 
      });
      return;
    }

    if (deliveryTimeOption === 'specific' && !selectedDeliveryTime) {
      setDialog({ 
        open: true, 
        type: "warning",
        title: "Delivery Time Required", 
        message: "Please select a preferred delivery time slot." 
      });
      return;
    }
  
  
    setLoading(true);
    const selectedSlot = deliveryTimeSlots.find(slot => slot.value === selectedDeliveryTime);

    try {
      const orderData = {
        user_email: user?.email,
        user_phone: user?.phone,
        order_type: 'prescription',
        items: [],
        prescription_urls: [],
        // delivery_address: address,
        // delivery_time_option: deliveryTimeOption,
        // delivery_time: deliveryTimeOption === 'specific' ? selectedDeliveryTime : '',
        delivery_address: {
          ...address,
          delivery_time_option: deliveryTimeOption,
          delivery_time: deliveryTimeOption === 'specific' ? selectedDeliveryTime : ''
        },
        subtotal: 0,
        delivery_charge: 0,
        discount: 0,
        total_amount: 0,
        patient_details: patientDetails,
        payment: {
          payment_id: `temp_${Date.now()}`,
          method: 'offline',
          status: 'pending',
          amount: 0
        },
        paymentDetails: {
          payment_id: `temp_${Date.now()}`,
          method: 'offline',
          status: 'pending',
          amount: 0
        },
        notes: `Prescription order - ${prescriptionFiles.length} file(s) uploaded`,
        preferred_medicines: medicineNames.filter(m => m.name.trim()).map(m => m.name.trim()),
      };
  
      const createdOrder = await createPrescriptionOrder(orderData);  
      console.log("Uploading prescriptions with order ID:", createdOrder.order_id);
      // const prescriptionUrls = await uploadPrescriptionToServer(
      //   prescriptionFiles, 
      //   createdOrder.order_id
      // );
      let prescriptionUrls = [];
      if (prescriptionFiles.length > 0) {
        console.log("Uploading prescriptions with order ID:", createdOrder.order_id);
        prescriptionUrls = await uploadPrescriptionToServer(
          prescriptionFiles, 
          createdOrder.order_id
        );
      }
  
      const updateResponse = await apiRequest(
        `${process.env.REACT_APP_MEDICINE_API_URL}/medicine-orders/${createdOrder.order_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          // body: JSON.stringify({ 
          //   prescription_urls: prescriptionUrls,
          //   delivery_time_option: deliveryTimeOption,
          //   delivery_time: deliveryTimeOption === 'specific' ? selectedDeliveryTime : ''
          // })
          body: JSON.stringify({ 
            prescription_urls: prescriptionUrls,
            // delivery_time_option: deliveryTimeOption,
            // delivery_time: deliveryTimeOption === 'specific' ? selectedDeliveryTime : '',
            delivery_time_option: deliveryTimeOption === 'specific' && selectedSlot ? selectedSlot.label : 'Anytime',
            delivery_time: deliveryTimeOption === 'specific' && selectedSlot 
              ? selectedSlot.fullDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })
              : '',
            preferred_medicines: medicineNames.filter(m => m.name.trim()).map(m => m.name.trim()),

          })
        }
      );
  
      if (!updateResponse.ok) {
        throw new Error('Failed to update order with prescription URLs');
      }
  
      const updatedOrder = await updateResponse.json();
      setOrder(updatedOrder);
      setTimeout(() => {
      setStep('confirmation');
    }, 300);
      
      setDialog({ 
        open: true, 
        type: "success",
        title: "Order Submitted", 
        message: `Your order has been submitted successfully! Order ID: ${updatedOrder.order.order_id}` 
      });
  
    } catch (error) {
      setDialog({ 
        open: true, 
        type: "error",
        title: "Submission Failed", 
        message: error.message || "Failed to submit order. Please try again or contact support." 
      });
      console.error("Order submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };


  // const steps = ['Patient Details', 'Upload Prescription', 'Delivery Details', 'Confirmation'];
  const steps = [ 'Upload Prescription','Patient Details', 'Delivery Details', 'Confirmation'];
  const getActiveStep = () => {
    switch(step) {
      case 'patient': return 1;
      case 'upload': return 0;
      case 'address': return 2;
      case 'confirmation': return 3;
      default: return 0;
    }
  };

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 10 }}>
        {/* Modern Header */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #006D77 0%, #005560 100%)',
          boxShadow: '0 4px 20px rgba(0,109,119,0.15)'
        }}>
          <Container maxWidth="md">
            <Box sx={{ py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                onClick={() => {
                  if (step === 'patient') setStep('upload');        // was: 'upload' → 'patient'
                  else if (step === 'address') setStep('patient');  // was: 'upload'
                  else if (step === 'confirmation') setStep('address');
                  else window.history.back();
                }}
                // onClick={() => {
                //   if (step === 'upload') setStep('patient');
                //   else if (step === 'address') setStep('upload');
                //   else if (step === 'confirmation') setStep('address');
                //   else window.history.back();
                // }}
                sx={{ 
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="700" sx={{ color: '#fff' }}>
                  Prescription Order
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Quick & Easy Medicine Delivery
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ mt: 3 }}>
          {/* Enhanced Progress Stepper */}
          <Paper elevation={0} sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            background: '#fff'
          }}>
            <Stepper activeStep={getActiveStep()} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel 
                    StepIconProps={{
                      sx: {
                        '&.Mui-active': { color: '#006D77' },
                        '&.Mui-completed': { color: '#10b981' }
                      }
                    }}
                  >
                    <Typography variant="caption" fontWeight={index === getActiveStep() ? 600 : 400}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          {/* STEP 1: Patient Details */}
          {step === 'patient' && (
            <PatientDetailsForm
              onComplete={handlePatientDetailsComplete}
              initialData={patientDetails}
              userEmail={user?.email}
              orderType="prescription"
              userData={user}
            />
          )}

          {/* STEP 2: Upload Prescription */}
          {step === 'upload' && (
            <Box>
              {/* Patient Summary Card */}
              {patientDetails && (
                <Card elevation={0} sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  border: '2px solid #e0f2f1',
                  background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Avatar sx={{ 
                        width: 70, 
                        height: 70, 
                        bgcolor: '#006D77',
                        fontSize: 28,
                        fontWeight: 700
                      }}>
                        {patientDetails.name?.charAt(0) || 'P'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={700} color="#0f172a">
                          {patientDetails.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            icon={<EventNote sx={{ fontSize: 18 }} />}
                            label={`Age: ${patientDetails.dob}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                          />
                          <Chip 
                            icon={<Wc sx={{ fontSize: 18 }} />}
                            label={patientDetails.gender}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                          />
                          <Chip 
                            icon={<Phone sx={{ fontSize: 18 }} />}
                            label={patientDetails.phone}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                          />
                        </Box>
                        {patientDetails.doctorname && (
                          <Typography variant="body2" sx={{ mt: 1, color: '#0f172a', fontWeight: 500 }}>
                            👨‍⚕️ Doctor: {patientDetails.doctorname}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Upload Card */}
              <Card elevation={0} sx={{ 
                mb: 3, 
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  p: 4,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2f1 100%)',
                  textAlign: 'center'
                }}>
                  <Box sx={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%',
                    bgcolor: '#006D77',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 8px 24px rgba(0,109,119,0.2)'
                  }}>
                    <PhotoLibrary sx={{ fontSize: 50, color: '#fff' }} />
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#0f172a" mb={1}>
                    Upload Your Prescription
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Upload clear images or PDFs of your prescription
                  </Typography>
                  <Button 
                    variant="contained" 
                    component="label" 
                    size="large"
                    startIcon={<PhotoLibrary />}
                    sx={{
                      bgcolor: '#006D77',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(0,109,119,0.3)',
                      '&:hover': { 
                        bgcolor: '#005560',
                        boxShadow: '0 6px 16px rgba(0,109,119,0.4)'
                      }
                    }}
                  >
                    Choose Files
                    <input type="file" accept="image/*,.pdf,.doc,.docx" hidden multiple onChange={handleFileSelect} />
                  </Button>
                  <Typography variant="caption" sx={{ 
                    display: 'block',
                    mt: 2,
                    color: '#64748b'
                  }}>
                    📎 Supported: JPG, PNG, PDF, DOC, DOCX (Max 10MB each)
                  </Typography>
                </Box>
              </Card>

              {/* Uploaded Files */}
              {uploading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={50} sx={{ color: '#006D77' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Processing files...
                  </Typography>
                </Box>
              )}

              {prescriptionFiles.length > 0 && (
                <Card elevation={0} sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>
                        📋 Uploaded Files ({prescriptionFiles.length})
                      </Typography>
                      <Chip 
                        label={`${prescriptionFiles.length} file${prescriptionFiles.length > 1 ? 's' : ''}`}
                        color="success"
                        size="small"
                      />
                    </Box>
                    <Grid container spacing={2}>
                      {prescriptionFiles.map((file) => (
                        <Grid item xs={12} sm={6} key={file.id}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                borderColor: '#006D77'
                              }
                            }}
                          >
                            {file.preview ? (
                              <Box 
                                component="img" 
                                src={file.preview}
                                sx={{ 
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: 1.5,
                                  border: '2px solid #e2e8f0'
                                }}
                              />
                            ) : (
                              <Box sx={{
                                width: 60,
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#f1f5f9',
                                borderRadius: 1.5,
                                border: '2px solid #e2e8f0'
                              }}>
                                <Description sx={{ fontSize: 30, color: '#64748b' }} />
                              </Box>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(file.size)}
                              </Typography>
                            </Box>
                            <IconButton 
                              onClick={() => handleRemoveFile(file.id)}
                              size="small"
                              sx={{ 
                                color: '#ef4444',
                                '&:hover': { bgcolor: '#fee2e2' }
                              }}
                            >
                              <Close />
                            </IconButton>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}
      <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
        <Box sx={{ flex: 1, height: "1px", bgcolor: "#D1D5DB" }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#6B7280", mx: 2 }}>
          OR
        </Typography>
        <Box sx={{ flex: 1, height: "1px", bgcolor: "#D1D5DB" }} />
      </Box>
      
{/* Medicine Names Input */}
<Card elevation={0} sx={{ 
  mb: 3,
  borderRadius: 3,
  border: '1px solid #e2e8f0'
}}>
  <CardContent sx={{ p: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <Typography variant="h6" fontWeight={700}>
        💊 Medicine Names <Typography component="span" variant="caption" color="text.secondary">(Optional)</Typography>
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary" mb={2}>
      You can list the medicines from your prescription.
    </Typography>

    {medicineNames.map((medicine, index) => (
      <Box key={medicine.id} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          label={`Medicine ${index + 1}`}
          placeholder="e.g. Paracetamol 500mg"
          value={medicine.name}
          onChange={(e) => handleMedicineChange(medicine.id, e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <IconButton
          onClick={() => handleRemoveMedicine(medicine.id)}
          disabled={medicineNames.length === 1}
          size="small"
          sx={{ 
            color: '#ef4444',
            '&:hover': { bgcolor: '#fee2e2' },
            '&:disabled': { color: '#cbd5e1' }
          }}
        >
          <Close />
        </IconButton>
      </Box>
    ))}

    <Button
      variant="outlined"
      size="small"
      onClick={handleAddMedicine}
      sx={{
        mt: 1,
        textTransform: 'none',
        borderRadius: 2,
        borderColor: '#006D77',
        color: '#006D77',
        '&:hover': { borderColor: '#005560', bgcolor: 'rgba(0,109,119,0.04)' }
      }}
    >
      + Add Another Medicine
    </Button>
  </CardContent>
</Card>











              {/* Guidelines */}
              <Card elevation={0} sx={{ 
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                bgcolor: '#fefce8',
                mb: 3
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} color="#854d0e" mb={2}>
                    📌 Prescription Guidelines
                  </Typography>
                  <List dense>
                    {[
                      'Ensure prescription is clear and readable',
                      'Include doctor\'s name & signature',
                      'Prescription should be valid (not expired)',
                      'All corners should be visible',
                      'Multiple pages can be uploaded'
                    ].map((text, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle sx={{ fontSize: 20, color: '#16a34a' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={text}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            color: '#422006'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              {/* Payment Method Card */}
              <Card elevation={0} sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                bgcolor: '#eff6ff'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} color="#1e40af" mb={1}>
                    💳 Payment Method
                  </Typography>
                  <Typography variant="body2" color="#374151">
                    This order will be processed as <strong>Cash on Delivery (COD)</strong>
                  </Typography>
                  <Typography variant="body2" color="#6B7280" sx={{ mt: 1 }}>
                    Pay when your medicines are delivered to your doorstep.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* STEP 3: Delivery Address & Time */}
          {step === 'address' && (
            <Box>
              {/* Patient Details Summary - Collapsible */}
              {patientDetails && (
                <Card elevation={0} sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box 
                      onClick={() => setShowPatientDetails(!showPatientDetails)}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#e0f2f1', color: '#006D77', width: 40, height: 40 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Patient Details
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {patientDetails.name} • {patientDetails.dob}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton size="small">
                        {showPatientDetails ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                    <Collapse in={showPatientDetails}>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Name</Typography>
                          <Typography variant="body2" fontWeight={600}>{patientDetails.name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Age</Typography>
                          <Typography variant="body2" fontWeight={600}>{patientDetails.dob}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Gender</Typography>
                          <Typography variant="body2" fontWeight={600}>{patientDetails.gender}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Phone</Typography>
                          <Typography variant="body2" fontWeight={600}>{patientDetails.phone}</Typography>
                        </Grid>
                        {patientDetails.doctorname && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Doctor</Typography>
                            <Typography variant="body2" fontWeight={600}>{patientDetails.doctorname}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Collapse>
                  </CardContent>
                </Card>
              )}

              {/* Prescription Files Summary - Collapsible */}
              {prescriptionFiles.length > 0 && (
                <Card elevation={0} sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box 
                      onClick={() => setShowPrescriptions(!showPrescriptions)}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#dbeafe', color: '#1e40af', width: 40, height: 40 }}>
                          <ImageIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Prescription Files
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {prescriptionFiles.length} file{prescriptionFiles.length > 1 ? 's' : ''} uploaded
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton size="small">
                        {showPrescriptions ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                    <Collapse in={showPrescriptions}>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={1.5}>
                        {prescriptionFiles.map((file) => (
                          <Grid item xs={4} sm={3} key={file.id}>
                            <Paper
                              elevation={0}
                              sx={{
                                position: 'relative',
                                paddingTop: '100%',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '2px solid #e2e8f0',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#006D77',
                                  boxShadow: '0 4px 12px rgba(0,109,119,0.15)'
                                }
                              }}
                            >
                              {file.preview ? (
                                <Box
                                  component="img"
                                  src={file.preview}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: '#f1f5f9'
                                }}>
                                  <Description sx={{ fontSize: 30, color: '#64748b' }} />
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </CardContent>
                </Card>
              )}

              {/* Address Form or Display */}
              {!isEditingAddress ? (
                <Card elevation={0} sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700}>
                        🏠 Delivery Address
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => setIsEditingAddress(true)}
                        sx={{ 
                          textTransform: 'none',
                          borderRadius: 2,
                          borderColor: '#006D77',
                          color: '#006D77',
                          '&:hover': {
                            borderColor: '#005560',
                            bgcolor: 'rgba(0,109,119,0.04)'
                          }
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Full Name</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.full_name}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.phone}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Community</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.community_name}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Block</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.address_line1}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Flat/Apartment</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.address_line2 || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{display:"none"}}>
                        <Typography variant="caption" color="text.secondary">Landmark</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.landmark || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{display:"none"}}>
                        <Typography variant="caption" color="text.secondary">City</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.city}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{display:"none"}}>
                        <Typography variant="caption" color="text.secondary">State</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.state}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{display:"none"}}>
                        <Typography variant="caption" color="text.secondary">Pincode</Typography>
                        <Typography variant="body2" fontWeight={600}>{address.pincode}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ) : (
                <Card elevation={0} sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Typography variant="h6" fontWeight={700}>
                        ✏️ Edit Delivery Address
                      </Typography>
                      <Button 
                        variant="text" 
                        size="small"
                        onClick={() => setIsEditingAddress(false)}
                        sx={{ 
                          textTransform: 'none',
                          color: '#64748b'
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          label="Full Name" 
                          fullWidth 
                          required
                          error={!!errors.full_name}
                          helperText={errors.full_name}
                          value={address.full_name}
                          onChange={(e) => {
                                setAddress({ ...address, full_name: e.target.value })
                                  // ✅ Validate only this field
                                  let error = "";
                                  if (!e.target.value.trim()) {
                                    error = "Full name is required";
                                  } else if (!/^[A-Za-z ]{1,}$/.test(e.target.value.trim())) {
                                    error = "Enter a valid name";
                                  }

                                  setErrors((prev) => ({
                                    ...prev,
                                    full_name: error
                                  }));

                          }}
                          size="small"
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={6}>
                        <TextField 
                          label="Phone Number" 
                          fullWidth 
                          required
                          value={address.phone}
                          onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                          size="small"
                        />
                      </Grid> */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Phone Number"
                          fullWidth
                          required
                          size="small"
                          value={address.phone}
                          error={!!errors.phone}
                          helperText={errors.phone}
                          inputProps={{
                            maxLength: 10,          
                            inputMode: 'numeric',   
                            pattern: '[0-9]*',
                          }}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ''); 
                            if (value.length <= 10) {
                              setAddress({ ...address, phone: value });
                              const error = validatePhone(value);
                              setErrors((prev) => ({ ...prev, phone: error }));
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField 
                          select 
                          label="Community/Society Name" 
                          fullWidth 
                          required
                          value={address.community_name}
                          disabled 
                          // onChange={(e) => setAddress({ ...address, community_name: e.target.value })}
                          size="small"
                          
                        >
                          <MenuItem value="">Select Community</MenuItem>
                          {COMMUNITY_OPTIONS.map((community) => (
                            <MenuItem key={community} value={community}>{community}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          select 
                          label="Block / Address Line 1" 
                          fullWidth 
                          required
                          value={address.address_line1}
                          onChange={(e) => setAddress({ ...address, address_line1: e.target.value })}
                          size="small"
                          sx={{width:"200%"}}
                        >
                          <MenuItem value="">Select Block</MenuItem>
                          {BLOCK_OPTIONS.map((block) => (
                            <MenuItem key={block} value={block}>{block}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          label="Flat/Apartment Number" 
                          fullWidth
                          value={address.address_line2}
                          onChange={(e) => setAddress({ ...address, address_line2: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{display:"none"}}>
                        <TextField 
                          label="Landmark" 
                          fullWidth
                          value={address.landmark}
                          onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{display:"none"}}>
                        <TextField 
                          label="City" 
                          fullWidth 
                          required
                          value={address.city}
                          onChange={(e) => setAddress({ ...address, city: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{display:"none"}}>
                        <TextField 
                          label="State" 
                          fullWidth 
                          required
                          value={address.state}
                          onChange={(e) => setAddress({ ...address, state: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{display:"none"}}>
                        <TextField 
                          label="Pincode" 
                          fullWidth 
                          required
                          value={address.pincode}
                          onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button 
                          variant="contained" 
                          fullWidth
                          disabled={Object.values(errors).some(err => err)}
                          onClick={() =>{
                            if(validateAddressForm){
                            setIsEditingAddress(false)}}}
                          sx={{
                            bgcolor: '#006D77',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.2,
                            '&:hover': { bgcolor: '#005560' }
                          }}
                        >
                          Save Address
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}


  
              {/* Delivery Time Selection Card */}
            <Card elevation={0} sx={{ 
              mb: 3,
              borderRadius: 3,
              border: '1px solid #e2e8f0'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AccessTime sx={{ color: '#006D77', fontSize: 28 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Delivery Time Preference
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose when you'd like to receive your order
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                {/* Delivery Option Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Delivery Option</InputLabel>
                  <Select
                    value={deliveryTimeOption}

                    onChange={(e) => {
                      console.log('📋 Option changed to:', e.target.value);
                      setDeliveryTimeOption(e.target.value);
                      setSelectedDate('');
                      setSelectedDeliveryTime('');
                      setDeliveryTimeSlots([]); 
                    }}
                    label="Delivery Option"
                  >
                    <MenuItem value="anytime">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box>🕐</Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Anytime</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Deliver at any convenient time
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="specific">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box>📅</Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Specific Time</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Choose a preferred date and time slot
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Show date and time selection only if "specific" is chosen */}
                {deliveryTimeOption === 'specific' && (
                  <>
                    {/* Date Selection */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Select Date</InputLabel>
                      <Select
                        value={selectedDate}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          // Clear everything first
                          setDeliveryTimeSlots([]);
                          setSelectedDeliveryTime('');
                          
                          // Then set new date (this will trigger useEffect)
                          setSelectedDate(newDate);
                        }}
                        label="Select Date"
                      >
                        {generateAvailableDates().map((date) => (
                          <MenuItem key={date.value} value={date.value}>
                             {date.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Time Slot Selection - Only show after date is selected */}
                    {selectedDate && (
                      <FormControl fullWidth>
                        <InputLabel>Select Time Slot</InputLabel>
                        <Select
                          key={`time-slots-${selectedDate}-${deliveryTimeSlots.length}`}
                          value={selectedDeliveryTime}
                          onChange={(e) => setSelectedDeliveryTime(e.target.value)}
                          label="Select Time Slot"
                        >
                          {deliveryTimeSlots.length > 0 ? (
                            deliveryTimeSlots.map((slot) => (
                              <MenuItem key={slot.value} value={slot.value}>
                                 {slot.label}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>
                              No available slots for this date
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    )}

                    {/* Selected Time Display */}
                    {selectedDeliveryTime && (
                      <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                          <strong>Selected:</strong> {new Date(selectedDeliveryTime).toLocaleString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}

                {/* Anytime Selection Confirmation */}
                {deliveryTimeOption === 'anytime' && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      ✅ Your order will be delivered at any convenient time within our delivery hours (9 AM - 8 PM)
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
            </Box>
          )}

          {/* STEP 4: Order Confirmation */}
          {step === 'confirmation' && order && (
            <Box>
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  border: '2px solid #10b981',
                  bgcolor: '#f0fdf4'
                }}
              >
                <Typography variant="body1" fontWeight={700} color="#166534">
                  🎉 Order Submitted Successfully!
                </Typography>
                <Typography variant="body2" color="#166534" sx={{ mt: 0.5 }}>
                  Order ID: <strong>{order.order_id}</strong>
                </Typography>
              </Alert>

              <Card elevation={0} sx={{ 
                mb: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    📦 Order Status
                  </Typography>
                  <Chip 
                    label="Waiting for Admin Review" 
                    color="warning" 
                    icon={<LocalHospital />}
                    sx={{ mb: 2, fontWeight: 600 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Our pharmacist will review your prescription and calculate the bill. 
                    You'll receive a notification once the bill is ready for approval.
                  </Typography>
                </CardContent>
              </Card>

              <Card elevation={0} sx={{ 
                mb: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    📄 Prescription Files
                  </Typography>
                  <Typography variant="body2" mb={2} color="text.secondary">
                    {prescriptionFiles.length} file(s) uploaded successfully
                  </Typography>
                  
                  {order.prescription_urls && order.prescription_urls.length > 0 && (
                    <Grid container spacing={2}>
                      {order.prescription_urls.map((url, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Paper 
                            elevation={2} 
                            sx={{ 
                              p: 1.5,
                              borderRadius: 2,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                                transform: 'translateY(-2px)'
                              }
                            }}
                            onClick={() => window.open(`${process.env.REACT_APP_MEDICINE_API_URL}${url}`, '_blank')}
                          >
                            <Box
                              component="img"
                              src={`${process.env.REACT_APP_MEDICINE_API_URL}${url}`}
                              alt={`Prescription ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 150,
                                objectFit: 'contain',
                                borderRadius: 1.5,
                                bgcolor: '#f8fafc'
                              }}
                            />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block', 
                                mt: 1, 
                                textAlign: 'center',
                                fontWeight: 600,
                                color: '#64748b'
                              }}
                            >
                              Prescription {index + 1}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}


{/* Medicines Requested - in confirmation */}
{medicineNames.some(m => m.name.trim()) && (
  <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} mb={2}>💊 Medicines Requested</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {medicineNames
          .filter(m => m.name.trim())
          .map((m, i) => (
            <Chip key={i} label={m.name} variant="outlined" color="primary" size="small" />
          ))}
      </Box>
    </CardContent>
  </Card>
)}




                </CardContent>
              </Card>

              <Card elevation={0} sx={{ 
                mb: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    🏠 Delivery Address
                  </Typography>
                  <Box sx={{ 
                    p: 2.5, 
                    bgcolor: '#f8fafc', 
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="body1" fontWeight={600} color="#0f172a">
                      {address.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {address.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {address.community_name}
                    </Typography>
                      {address.address_line1 && (
                      <Typography variant="body2" color="text.secondary">
                        Block: {address.address_line1}
                      </Typography>
                      )}
                      {address.address_line2 && (
                      <Typography variant="body2" color="text.secondary">
                        Flat No: {address.address_line2}
                      </Typography>
                      )}
                    {/* {address.landmark && (
                      <Typography variant="body2" color="text.secondary">
                        Landmark: {address.landmark}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {address.city}, {address.state} - {address.pincode}
                    </Typography> */}
                  </Box>
                </CardContent>
              </Card>


              {/* Delivery Time Display */}
              <Card elevation={0} sx={{ 
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                bgcolor: '#f0fdf4'
              }}>
 <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AccessTime sx={{ color: '#16a34a', fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} color="#166534">
              ⏰ Delivery Schedule
            </Typography>
            <Typography variant="caption" color="#16a34a">
              Your preferred delivery time
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 2, borderColor: '#86efac' }} />
        
        {order?.delivery_address && (<Box sx={{ 
          p: 2.5, 
          bgcolor: 'rgba(255,255,255,0.7)',
          borderRadius: 2,
          border: '1px solid #86efac'
        }}>
          {order?.delivery_address?.delivery_time_option === 'anytime' ? (
            <>
              <Typography variant="body1" fontWeight={600} color="#166534" sx={{ mb: 1 }}>
                📦 Deliver Anytime
              </Typography>
              <Typography variant="body2" color="#15803d">
                Your order will be delivered at any convenient time during our delivery hours (6 AM - 10 PM)
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="caption" color="#15803d" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                📅 SCHEDULED DELIVERY
              </Typography>
              <Typography variant="h6" fontWeight={700} color="#166534" sx={{ mb: 2 }}>
                {new Date(order.delivery_address.delivery_time).toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </Typography>
              <Box sx={{ 
                pt: 2, 
                borderTop: '1px dashed #86efac' 
              }}>
                <Typography variant="caption" color="#15803d">
                  📌 Date: {new Date(order.delivery_address.delivery_time).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
                <br />
                <Typography variant="caption" color="#15803d">
                  🕐 Time: {new Date(order.delivery_address.delivery_time).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Typography>
              </Box>
            </>
            
          )}
        </Box>)}
      </CardContent>
              </Card>
            </Box>
          )}
        </Container>

        {/* Bottom Action Button */}
        <Box sx={{
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          bgcolor: '#fff', 
          borderTop: '2px solid #e2e8f0',
          p: 2,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
          zIndex: 1000
        }}>
          <Container maxWidth="md">
            {step === 'upload' && (
              <Button 
                variant="contained" 
                size="large" 
                fullWidth
                // disabled={prescriptionFiles.length === 0 || uploading}
                // CHANGE the disabled prop on the Proceed button:
                disabled={
                  (prescriptionFiles.length === 0 && medicineNames.every(m => !m.name.trim())) || uploading
                }
                onClick={handleProceedToAddress}
                sx={{
                  bgcolor: '#006D77', 
                  py: 1.8, 
                  textTransform: 'none',
                  fontSize: 16, 
                  fontWeight: 700,
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,109,119,0.3)',
                  '&:hover': { 
                    bgcolor: '#005560',
                    boxShadow: '0 6px 16px rgba(0,109,119,0.4)'
                  },
                  '&:disabled': {
                    bgcolor: '#94a3b8',
                    color: '#fff'
                  }
                }}
              >
                Continue →
              </Button>
            )}
            {step === 'address' && (
              <Button 
                variant="contained" 
                size="large" 
                fullWidth 
                disabled={loading}
                onClick={handleSubmitOrder}
                sx={{
                  bgcolor: '#006D77', 
                  py: 1.8, 
                  textTransform: 'none',
                  fontSize: 16, 
                  fontWeight: 700,
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,109,119,0.3)',
                  '&:hover': { 
                    bgcolor: '#005560',
                    boxShadow: '0 6px 16px rgba(0,109,119,0.4)'
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                    <span>Submitting Order...</span>
                  </Box>
                ) : (
                  'Submit Order'
                )}
              </Button>
            )}
            {step === 'confirmation' && (
              <Button 
                variant="contained" 
                size="large" 
                fullWidth
                onClick={() => window.location.href = '/my-orders'}
                sx={{
                  bgcolor: '#006D77', 
                  py: 1.8, 
                  textTransform: 'none',
                  fontSize: 16, 
                  fontWeight: 700,
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,109,119,0.3)',
                  '&:hover': { 
                    bgcolor: '#005560',
                    boxShadow: '0 6px 16px rgba(0,109,119,0.4)'
                  }
                }}
              >
                View My Orders
              </Button>
            )}
          </Container>
        </Box>

        {/* Remove File Dialog */}
        <Dialog 
          open={removeDialogOpen} 
          onClose={() => setRemoveDialogOpen(false)}
          PaperProps={{
            sx: { borderRadius: 3, maxWidth: 400 }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Remove File?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to remove this prescription file? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 0 }}>
            <Button 
              onClick={() => setRemoveDialogOpen(false)}
              sx={{ 
                textTransform: 'none',
                color: '#64748b'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmRemoveFile} 
              color="error" 
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)'
                }
              }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* CustomDialog - Outside the main Box */}
      <CustomDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onClose={() => setDialog({ ...dialog, open: false })}
      />
    </>
  );
};

export default PrescriptionUploadScreen;